import React, { useState, useRef, useEffect, useCallback } from 'react';
import WhatsAppPreview from './WhatsAppPreview';
import MediaUpload from './MediaUpload';
import './CampaignsPage.css';

const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/whatsapp`;

// --- Shared Helpers ---

const resolveVariableValue = (mapping, contact = null) => {
  if (!mapping) return '';
  if (mapping.type === 'fix') {
    return mapping.value || '';
  } else if (mapping.type === 'csv') {
    const value = contact ? contact[mapping.value] : '';
    return (!value || value.trim() === '') && mapping.fallback
      ? mapping.fallback
      : (value || '');
  }
  return '';
};

const buildTemplateVars = (templateVariables) => {
  const vars = {};
  Object.keys(templateVariables).forEach(varName => {
    vars[`var_${varName}`] = `{{${varName}}}`;
  });
  return vars;
};

const resolveHeaderMediaPayload = ({
  hasHeaderMedia,
  getHeaderMediaType,
  headerMediaId,
  headerMediaUrl,
  variables = {}
}) => {
  if (!hasHeaderMedia()) return {};

  const payload = {
    template_header_type: getHeaderMediaType()
  };

  // Check override from variables (test mode) first
  if (variables.headerMediaId || headerMediaId) {
    payload.header_media_id = variables.headerMediaId || headerMediaId;
  } else {
    payload.header_media_url = variables.headerMediaUrl || headerMediaUrl;
  }
  return payload;
};

const buildCarouselCardsPayload = (templateType, carouselCards, template) => {
  if (!templateType.startsWith('CAROUSEL')) return [];

  const templateCarousel = template.components.find(c => c.type === 'CAROUSEL');
  const templateCards = templateCarousel ? templateCarousel.cards : [];

  if (templateType === 'CAROUSEL_PRODUCT') {
    return carouselCards.map((c, i) => ({
      header_type: 'product',
      catalog_id: c.catalog_id,
      product_retailer_id: c.product_retailer_id,
      body_variables: {} // Card specific body vars not implemented in UI yet
    }));
  } else if (templateType === 'CAROUSEL_MEDIA') {
    return carouselCards.map((c, i) => {
      const templateCardHeader = templateCards[i]?.components?.find(cmp => cmp.type === 'HEADER');
      const headerType = (templateCardHeader?.format || 'IMAGE').toLowerCase();

      const payload = {
        header_type: headerType,
        header_media_id: c.media_id || undefined,
        header_media_url: !c.media_id ? c.media_url : undefined,
        body_variables: {},
        buttons: [] // Could be populated if we parsed card buttons
      };

      // Filter out undefined keys
      return Object.fromEntries(Object.entries(payload).filter(([_, v]) => v !== undefined));
    });
  }
  return [];
};

const buildMpmConfig = (catalogDetails, mpmSections) => ({
  thumbnail_product_retailer_id: catalogDetails.thumbnail_product_retailer_id,
  sections: mpmSections
});

const buildCatalogConfig = (catalogDetails) => ({
  catalog_id: catalogDetails.catalog_id,
  product_retailer_id: catalogDetails.product_retailer_id,
  thumbnail_product_retailer_id: catalogDetails.thumbnail_product_retailer_id
});

const BroadcastModal = ({
  token,
  show,
  onClose,
  initialContacts = [],
  onSuccess,
  showCsvImport = true
}) => {
  const [contacts, setContacts] = useState(initialContacts);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [step, setStep] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [headerMap, setHeaderMap] = useState({ name: '', phone: '' });
  const [csvContacts, setCsvContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [campaignName, setCampaignName] = useState('');
  const [sending, setSending] = useState(false);
  const [retryAfterHours, setRetryAfterHours] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testSent, setTestSent] = useState(false);
  const [refreshingTemplates, setRefreshingTemplates] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerMediaId, setHeaderMediaId] = useState(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaUploadError, setMediaUploadError] = useState(null);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [fallbackValues, setFallbackValues] = useState({});
  const [emptyCsvVars, setEmptyCsvVars] = useState([]);
  const [retryCount, setRetryCount] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [showTestVariablesModal, setShowTestVariablesModal] = useState(false);
  const [testVariables, setTestVariables] = useState({});
  const [showCsvModal, setShowCsvModal] = useState(false);

  // Advanced Template States
  const [templateType, setTemplateType] = useState('STANDARD'); // STANDARD, CATALOG, MPM, CAROUSEL
  const [catalogDetails, setCatalogDetails] = useState({
    catalog_id: '',
    product_retailer_id: '', // For header
    thumbnail_product_retailer_id: '' // For button
  });
  const [mpmSections, setMpmSections] = useState([
    { title: 'Section 1', product_items: [{ product_retailer_id: '' }] }
  ]);
  const [carouselCards, setCarouselCards] = useState([]);

  const fileInputRef = useRef();

  // Initialize contacts when initialContacts changes or modal opens
  useEffect(() => {
    if (show && initialContacts.length > 0) {
      setContacts(initialContacts);
      setSelectedContacts(initialContacts);
    } else if (show && initialContacts.length === 0) {
      setContacts([]);
      setSelectedContacts([]);
    }
  }, [show, initialContacts]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setStep(0);
      setSelectedContacts([]);
      setSelectedTemplate(null);
      setTemplateVariables({});
      setTemplateVariables({});
      setCampaignName('');
      setRetryCount('');
      setHeaderMediaUrl('');
      setHeaderMediaId(null);
      setMediaUploadError(null);
      setRetryAfterHours('');
      setTestPhone('');
      setTestVariables({});
      setContacts(initialContacts);
      setTemplateType('STANDARD');
      setCatalogDetails({ catalog_id: '', product_retailer_id: '', thumbnail_product_retailer_id: '' });
      setMpmSections([{ title: 'Section 1', product_items: [{ product_retailer_id: '' }] }]);
      setCarouselCards([]);
    }
  }, [show, initialContacts]);

  // Check if template has header with media
  const hasHeaderMedia = () => {
    if (!selectedTemplate || !selectedTemplate.components) return false;
    const headerComponent = selectedTemplate.components.find(c => c.type === 'HEADER');
    return headerComponent && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format);
  };

  // Get header media type
  const getHeaderMediaType = () => {
    if (!selectedTemplate || !selectedTemplate.components) return null;
    const headerComponent = selectedTemplate.components.find(c => c.type === 'HEADER');
    return headerComponent ? headerComponent.format.toLowerCase() : null;
  };

  // Fetch templates from API
  const fetchTemplates = useCallback(async () => {
    try {
      setRefreshingTemplates(true);
      const response = await fetch(`${BASE_URL}/get_templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setTemplates(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setRefreshingTemplates(false);
    }
  }, [token]);

  // Fetch templates when modal opens
  useEffect(() => {
    if (show && token) {
      fetchTemplates();
    }
  }, [show, token, fetchTemplates]);

  // Check if all template variables are properly mapped
  // Check if all template variables are properly mapped
  const areAllVariablesMapped = () => {
    if (!selectedTemplate) return false;

    // Check header media first - need either URL or ID
    if (hasHeaderMedia() && !headerMediaUrl.trim() && !headerMediaId) return false;

    if (Object.keys(templateVariables).length === 0) return true;

    return Object.keys(templateVariables).every(varName => {
      const mapping = templateVariables[varName];
      if (!mapping || !mapping.type) return false;

      if (mapping.type === 'fix') {
        return mapping.value && mapping.value.trim() !== '';
      } else if (mapping.type === 'csv') {
        return mapping.value && mapping.value.trim() !== '';
      }
      return false;
    });

    // Validate complex template fields
    // Move return true to end, check constraints first

    if (templateType === 'SPM') {
      if (!catalogDetails.catalog_id || !catalogDetails.product_retailer_id) return false;
    } else if (templateType === 'CATALOG') {
      if (!catalogDetails.thumbnail_product_retailer_id) return false;
    } else if (templateType === 'MPM') {
      // Validate at least one section and one product
      if (mpmSections.length === 0) return false;
      for (const section of mpmSections) {
        if (!section.title) return false;
        if (section.product_items.length === 0) return false;
        if (section.product_items.some(p => !p.product_retailer_id)) return false;
      }
    } else if (templateType === 'CAROUSEL_PRODUCT') {
      if (carouselCards.some(c => !c.product_retailer_id || !c.catalog_id)) return false;
    } else if (templateType === 'CAROUSEL_MEDIA') {
      // Check if every card has either a media ID or URL
      if (carouselCards.some(c => !c.media_id && !c.media_url)) return false;
    }

    return true;
  };

  // Check if any CSV variables have empty values in contacts
  const getEmptyCsvVariables = () => {
    const emptyVars = [];
    Object.keys(templateVariables).forEach(varName => {
      const mapping = templateVariables[varName];
      if (mapping && mapping.type === 'csv') {
        const hasEmptyValues = selectedContacts.some(contact => {
          const value = contact[mapping.value];
          return !value || value.trim() === '';
        });
        if (hasEmptyValues) {
          emptyVars.push({ varName, csvColumn: mapping.value });
        }
      }
    });
    return emptyVars;
  };

  // Handle Media File Upload (Unified for Header and Cards)
  const handleMediaUpload = async (e, cardIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    // Frontend Validation
    const MEDIA_RULES = {
      "image/jpeg": 5 * 1024 * 1024,
      "image/png": 5 * 1024 * 1024,
      "video/mp4": 16 * 1024 * 1024,
      "video/3gpp": 16 * 1024 * 1024,
      "audio/mpeg": 16 * 1024 * 1024,
      "audio/aac": 16 * 1024 * 1024,
      "application/pdf": 10 * 1024 * 1024,
    };

    if (!MEDIA_RULES[file.type]) {
      setMediaUploadError(`Invalid file type: ${file.type}. Allowed: JPEG, PNG, MP4, 3GPP, MPEG, AAC, PDF`);
      return;
    }

    if (file.size > MEDIA_RULES[file.type]) {
      const limitMB = MEDIA_RULES[file.type] / (1024 * 1024);
      setMediaUploadError(`File too large. Limit for ${file.type} is ${limitMB}MB`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingMedia(true);
      setMediaUploadError(null);

      const response = await fetch(`${BASE_URL}/upload_media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        let errorMsg = 'Upload failed';
        try {
          const errData = await response.json();
          errorMsg = errData.detail || errData.message || 'Upload failed';
        } catch (e) {
          errorMsg = `Upload failed: ${response.statusText}`;
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();

      let mediaId = null;
      if (result.data && result.data.id) {
        mediaId = result.data.id;
      } else if (result.id) {
        mediaId = result.id;
      }

      if (mediaId) {
        if (cardIndex !== null) {
          // Update Carousel Card
          const newCards = [...carouselCards];
          newCards[cardIndex].media_id = mediaId;
          newCards[cardIndex].media_url = '';

          if (file.type.startsWith('image/')) {
            newCards[cardIndex].media_preview_url = URL.createObjectURL(file);
          } else {
            newCards[cardIndex].media_preview_url = '';
          }
          setCarouselCards(newCards);
        } else {
          // Update Header
          setHeaderMediaId(mediaId);
          setHeaderMediaUrl('');

          if (file.type.startsWith('image/')) {
            const previewUrl = URL.createObjectURL(file);
            setHeaderMediaPreviewUrl(previewUrl);
          } else {
            setHeaderMediaPreviewUrl('');
          }
        }
      } else {
        throw new Error('No media ID returned from upload');
      }

    } catch (err) {
      console.error('Media upload error:', err);
      setMediaUploadError(err.message);
      if (cardIndex === null) {
        setHeaderMediaId(null);
      }
    } finally {
      setUploadingMedia(false);
    }
  };



  const handleRemoveMedia = () => {
    setHeaderMediaId(null);
    setHeaderMediaUrl('');
    if (headerMediaPreviewUrl) {
      URL.revokeObjectURL(headerMediaPreviewUrl);
      setHeaderMediaPreviewUrl('');
    }
    setMediaUploadError(null);
    // Reset file input if possible, or just rely on state

  };

  const handleNextStep = () => {
    if (step === 1) {
      const emptyVars = getEmptyCsvVariables();
      if (emptyVars.length > 0) {
        setEmptyCsvVars(emptyVars);
        setShowFallbackModal(true);
        return;
      }
    }
    setStep(step + 1);
  };

  const handleFallbackSubmit = () => {
    const updatedVariables = { ...templateVariables };
    Object.keys(fallbackValues).forEach(varName => {
      if (fallbackValues[varName]) {
        updatedVariables[varName] = {
          type: 'csv',
          value: templateVariables[varName].value,
          fallback: fallbackValues[varName]
        };
      }
    });
    setTemplateVariables(updatedVariables);
    setShowFallbackModal(false);
    setFallbackValues({});
    setEmptyCsvVars([]);
    setStep(step + 1);
  };

  // Prepare customer data with variables
  const prepareCustomerData = (contact) => {
    const variables = {};
    Object.keys(templateVariables).forEach(varName => {
      variables[varName] = resolveVariableValue(templateVariables[varName], contact);
    });
    return variables;
  };

  // CSV Upload and Contact Mapping
  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(Boolean);
      if (lines.length < 2) return;
      const headers = lines[0].split(',').map(h => h.trim());
      setCsvHeaders(headers);
      const data = lines.slice(1).map(line => {
        const values = line.split(',');
        const obj = {};
        headers.forEach((h, i) => { obj[h] = values[i] ? values[i].trim() : ''; });
        return obj;
      });
      setCsvContacts(data);
    };
    reader.readAsText(file);
  };

  const handleHeaderMapChange = (csvHeader, ourHeader) => {
    setHeaderMap(prev => ({ ...prev, [ourHeader]: csvHeader }));
  };

  const handleAddCsvContacts = () => {
    const mapped = csvContacts.map(c => ({
      id: contacts.length + Math.random(),
      name: c[headerMap.name],
      phone: c[headerMap.phone],
    })).filter(c => c.name && c.phone);
    setContacts(prev => [...prev, ...mapped]);
    setShowCsvModal(false);
    setCsvContacts([]);
    setCsvHeaders([]);
    setHeaderMap({ name: '', phone: '' });
  };

  // Contact selection
  const handleContactSelect = (contact, checked) => {
    setSelectedContacts(prev =>
      checked ? [...prev, contact] : prev.filter(c => c !== contact)
    );
  };

  // Template selection
  const handleTemplateSelect = (tpl) => {
    setShowTemplateDropdown(false);
    if (!tpl) return;

    setSelectedTemplate(tpl);
    setTemplateSearchTerm('');

    // Initialize variable mapping
    if (tpl.components) {
      const bodyComponent = tpl.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const variables = [];
        const regex = /\{\{(\d+)\}\}/g;
        let match;
        while ((match = regex.exec(bodyComponent.text)) !== null) {
          variables.push(match[1]);
        }
        const initialVars = {};
        variables.forEach(v => { initialVars[v] = { type: '', value: '' }; });
        setTemplateVariables(initialVars);
      }
    }

    // Reset header media
    const headerComponent = tpl.components.find(c => c.type === 'HEADER');
    if (headerComponent && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)) {
      setHeaderMediaUrl('');
      setHeaderMediaId(null);
      if (headerMediaPreviewUrl) {
        URL.revokeObjectURL(headerMediaPreviewUrl);
        setHeaderMediaPreviewUrl('');
      }
    }


    // Detect Template Type and Layout
    let type = 'STANDARD';
    let newCarouselCards = [];

    // Check Button Types first
    const buttonsComponent = tpl.components.find(c => c.type === 'BUTTONS');
    const firstButton = buttonsComponent?.buttons?.[0];

    // Check header type for SPM even if buttons text says something else or no buttons
    // User rule: if header format is product, it as a spm template
    // headerComponent is already defined above
    if (headerComponent && headerComponent.format === 'PRODUCT') {
      type = 'SPM';
    } else if (firstButton?.type.toLowerCase() === 'catalog' || firstButton?.type.toLowerCase() === 'oa_catalog' || (buttonsComponent && buttonsComponent.buttons?.some(b => b.type === 'CATALOG' || b.sub_type === 'CATALOG'))) {
      type = 'CATALOG';
    } else if (firstButton?.type.toLowerCase() === 'mpm' || firstButton?.sub_type === 'mpm') {
      type = 'MPM';
    } else {
      // Check for Carousel
      const carouselComponent = tpl.components.find(c => c.type === 'CAROUSEL');
      if (carouselComponent && carouselComponent.cards && carouselComponent.cards.length > 0) {
        const firstCard = carouselComponent.cards[0];
        const firstCardHeader = firstCard.components.find(c => c.type === 'HEADER');

        if (firstCardHeader?.format === 'PRODUCT') {
          type = 'CAROUSEL_PRODUCT';
          // Initialize cards for Product Carousel
          newCarouselCards = carouselComponent.cards.map((card, index) => ({
            card_index: index,
            product_retailer_id: '',
            catalog_id: ''
          }));
        } else if (firstCardHeader?.format === 'IMAGE' || firstCardHeader?.format === 'VIDEO') {
          type = 'CAROUSEL_MEDIA';
          // Initialize cards for Media Carousel
          newCarouselCards = carouselComponent.cards.map((card, index) => ({
            card_index: index,
            media_type: firstCardHeader.format.toLowerCase(),
            media_id: null,
            media_url: '',
            media_preview_url: '' // for local preview
          }));
        }
      }
    }

    setCarouselCards(newCarouselCards);
    setTemplateType(type);
  };

  // Variable mapping
  const handleVariableChange = (varName, val) => {
    setTemplateVariables(prev => ({ ...prev, [varName]: val }));
  };

  // Get template body text
  const getTemplateBody = () => {
    if (!selectedTemplate || !selectedTemplate.components) return '';
    const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
    return bodyComponent ? bodyComponent.text : '';
  };

  // Get template components
  const getTemplateComponent = (type) => {
    if (!selectedTemplate || !selectedTemplate.components) return null;
    return selectedTemplate.components.find(c => c.type === type);
  };

  // Render Template Preview
  const renderTemplatePreview = () => {
    if (!selectedTemplate) return <div style={{ color: '#999', fontStyle: 'italic', padding: 20, textAlign: 'center' }}>Select a template to view preview</div>;

    const header = getTemplateComponent('HEADER');
    const body = getTemplateComponent('BODY');
    const footer = getTemplateComponent('FOOTER');
    const buttons = getTemplateComponent('BUTTONS');

    // Body Text with Variables
    let bodyText = body ? body.text : '';
    Object.keys(templateVariables).forEach(varName => {
      const mapping = templateVariables[varName];
      // Use 'csv' logic with first contact if available for preview context
      const contact = selectedContacts.length > 0 ? selectedContacts[0] : null;
      const value = resolveVariableValue(mapping, contact);

      bodyText = bodyText.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), value || `{{${varName}}}`);
    });

    // Carousel Preview
    if (templateType.startsWith('CAROUSEL')) {
      const carouselComponent = getTemplateComponent('CAROUSEL');
      const staticCards = carouselComponent ? carouselComponent.cards : [];

      const combinedCards = carouselCards.map((c, i) => {
        const staticCard = staticCards[i] || {};
        const cardHeader = staticCard.components?.find(cmp => cmp.type === 'HEADER');
        const cardBody = staticCard.components?.find(cmp => cmp.type === 'BODY');
        const cardButtons = staticCard.components?.find(cmp => cmp.type === 'BUTTONS');

        let mediaPreviewUrl = '';
        if (templateType === 'CAROUSEL_MEDIA') {
          mediaPreviewUrl = c.media_preview_url || c.media_url;
        }

        let body = cardBody?.text || 'Card Body';
        // Replace vars in card body if we had card-specific vars mapping (advanced)
        // For now, raw text

        return {
          headerType: templateType === 'CAROUSEL_PRODUCT' ? 'PRODUCT' : ((cardHeader && cardHeader.format) || 'IMAGE'),
          mediaPreviewUrl: mediaPreviewUrl,
          bodyText: body,
          buttonMode: templateType === 'CAROUSEL_PRODUCT' ? 'DEFAULT' : 'CUSTOM',
          buttons: cardButtons?.buttons?.map(b => ({ type: b.type, text: b.text })) || []
        };
      });

      return (
        <WhatsAppPreview
          header={{ type: 'NONE' }}
          body={bodyText}
          footer={footer ? footer.text : ''}
          buttons={buttons ? buttons.buttons : []}
          carousel={{
            type: templateType === 'CAROUSEL_PRODUCT' ? 'PRODUCT' : 'MEDIA',
            cards: combinedCards
          }}
        />
      );
    }


    return (
      <WhatsAppPreview
        header={{
          type: header ? header.format : 'NONE',
          text: header && header.format === 'TEXT' ? header.text : null,
          mediaPreviewUrl: headerMediaPreviewUrl || headerMediaUrl
        }}
        body={bodyText}
        footer={footer ? footer.text : ''}
        buttons={buttons ? buttons.buttons : []}
      />
    );
  };

  // Send test campaign
  const handleSendTest = async () => {
    if (!testPhone.trim()) {
      alert('Please enter a phone number');
      return;
    }

    if (Object.keys(templateVariables).length > 0) {
      setTestVariables({});
      setShowTestVariablesModal(true);
      return;
    }

    await sendTestMessage({});
  };

  // Actually send the test message
  const sendTestMessage = async (variables) => {
    try {
      setTestSent(true);

      const { headerMediaUrl: _, ...customerVariables } = variables;
      const testCustomer = {
        phone: testPhone,
        name: 'Test User',
        variables: customerVariables
      };

      const templateVars = buildTemplateVars(templateVariables);

      const payload = {
        campaign_name: `${campaignName}_test`,
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        template_variables: templateVars,
        customers: [testCustomer]
      };

      const mediaPayload = resolveHeaderMediaPayload({
        hasHeaderMedia,
        getHeaderMediaType,
        headerMediaId,
        headerMediaUrl,
        variables
      });
      Object.assign(payload, mediaPayload);

      // Handle Complex Configurations
      if (templateType === 'SPM' || templateType === 'CATALOG') {
        payload.catalog_config = buildCatalogConfig(catalogDetails);
      } else if (templateType === 'MPM') {
        payload.mpm_config = buildMpmConfig(catalogDetails, mpmSections);
      } else if (templateType.startsWith('CAROUSEL')) {
        payload.carousel_cards = buildCarouselCardsPayload(templateType, carouselCards, selectedTemplate);
      }

      const response = await fetch(`${BASE_URL}/message/send_bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert('Test message sent successfully!');
        setTestPhone('');
        setTestVariables({});
      } else {
        throw new Error(result.message || 'Failed to send test message');
      }
    } catch (err) {
      console.error('Error sending test message:', err);
      alert(`Error sending test message: ${err.message}`);
    } finally {
      setTimeout(() => setTestSent(false), 1500);
    }
  };

  // Send campaign
  const handleSend = async () => {
    try {
      setSending(true);

      const customers = selectedContacts.map(contact => {
        const variables = prepareCustomerData(contact);

        return {
          phone: contact.phone,
          name: contact.name,
          variables
        };
      });

      const templateVars = buildTemplateVars(templateVariables);

      const payload = {
        campaign_name: campaignName,
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        template_variables: templateVars,
        customers
      };

      if (retryAfterHours !== '' && !Number.isNaN(Number(retryAfterHours))) {
        payload.retry_after_hours = Number(retryAfterHours);
      }
      if (retryCount !== '' && !Number.isNaN(Number(retryCount))) {
        payload.retry_count = Number(retryCount);
      }

      const mediaPayload = resolveHeaderMediaPayload({
        hasHeaderMedia,
        getHeaderMediaType,
        headerMediaId,
        headerMediaUrl
      });
      Object.assign(payload, mediaPayload);

      // Handle Complex Configurations
      if (templateType === 'SPM' || templateType === 'CATALOG') {
        payload.catalog_config = buildCatalogConfig(catalogDetails);
      } else if (templateType === 'MPM') {
        payload.mpm_config = buildMpmConfig(catalogDetails, mpmSections);
      } else if (templateType.startsWith('CAROUSEL')) {
        payload.carousel_cards = buildCarouselCardsPayload(templateType, carouselCards, selectedTemplate);
      }

      const response = await fetch(`${BASE_URL}/message/send_bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        onClose();
        alert('Campaign sent successfully!');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(result.message || 'Failed to send campaign');
      }
    } catch (err) {
      console.error('Error sending campaign:', err);
      alert(`Error sending campaign: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Stepper info
  const stepNames = [
    'Select Contacts',
    'Create Campaign',
    'Review & Test',
  ];

  if (!show) return null;

  return (
    <>
      {/* Broadcast Modal */}
      <div className="broadcast-modal-overlay">
        <div className="broadcast-modal">
          <button className="broadcast-close-btn" onClick={handleClose} style={{ position: 'absolute', right: 16, top: 16, zIndex: 10 }}>X</button>
          <div className="broadcast-stepper-header modern-stepper-header">
            <span className="stepper-label">Step {step + 1} / 3</span>
            <span className="stepper-name">{stepNames[step]}</span>
          </div>
          <div className="broadcast-modal-content">


            {/* Step 1: Select Contacts */}
            {step === 0 && (
              <div className="broadcast-stepper">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <b>Total Contacts:</b> {contacts.length} &nbsp; | &nbsp;
                    <b>Selected:</b> {selectedContacts.length}
                  </div>
                  {showCsvImport && (
                    <button className="broadcast-button" onClick={() => setShowCsvModal(true)} style={{ marginTop: 0, marginBottom: 0 }}>Import from CSV</button>
                  )}
                </div>
                <div className="broadcast-contacts-list" style={{ padding: 0, minHeight: 350, maxHeight: 500, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 40, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedContacts.length === contacts.length && contacts.length > 0}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedContacts(contacts);
                              } else {
                                setSelectedContacts([]);
                              }
                            }}
                          />
                        </th>
                        <th>Name</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c) => (
                        <tr key={c.id}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedContacts.includes(c)}
                              onChange={e => handleContactSelect(c, e.target.checked)}
                            />
                          </td>
                          <td>{c.name}</td>
                          <td>{c.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* Step 2: Create Campaign */}
            {step === 1 && (
              <div className="broadcast-stepper">
                <input
                  type="text"
                  placeholder="Campaign Name"
                  value={campaignName}
                  onChange={e => setCampaignName(e.target.value)}
                  style={{ marginBottom: 16 }}
                />

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.85em', color: '#666' }}>Retry After (Hours)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="e.g. 24"
                      value={retryAfterHours}
                      onChange={e => setRetryAfterHours(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.85em', color: '#666' }}>Retry Count</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="e.g. 1"
                      value={retryCount}
                      onChange={e => setRetryCount(e.target.value)}
                      min="0"
                      max="3"
                    />
                  </div>
                </div>

                <div className="template-selection-row">
                  <div className="template-select-container" style={{ position: 'relative' }}>
                    <div className="template-dropdown-trigger" onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedTemplate ? selectedTemplate.name : 'Choose a template...'}
                      </span>
                      <span>▼</span>
                    </div>

                    {showTemplateDropdown && (
                      <div className="template-dropdown-menu">
                        <div className="template-dropdown-search">
                          <input
                            type="text"
                            placeholder="Search templates..."
                            value={templateSearchTerm}
                            onChange={e => setTemplateSearchTerm(e.target.value)}
                            autoFocus
                            style={{ margin: 0 }}
                          />
                        </div>
                        {templates
                          .filter(t => t.name.toLowerCase().includes(templateSearchTerm.toLowerCase()))
                          .map(tpl => (
                            <div
                              key={tpl.id}
                              className="template-dropdown-item"
                              onClick={() => handleTemplateSelect(tpl)}
                            >
                              <div>
                                <div style={{ fontWeight: 500 }}>{tpl.name}</div>
                                <div style={{ fontSize: '0.85em', color: '#666' }}>{tpl.language}</div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span
                                  className={`template-status-dot status-${tpl.status.toLowerCase().includes('approved') ? 'approved' : tpl.status.toLowerCase().includes('rejected') ? 'rejected' : 'pending'}`}
                                  title={tpl.status}
                                />
                              </div>
                            </div>
                          ))}
                        <div
                          className="template-create-new"
                          onClick={() => window.open('https://business.facebook.com/latest/settings/whatsapp_account', '_blank')}
                        >
                          + Create New Template ↗
                        </div>
                      </div>
                    )}
                  </div>
                  <button className="broadcast-refresh-btn" onClick={fetchTemplates} disabled={refreshingTemplates} title="Refresh templates">
                    {refreshingTemplates ? '...' : '⟳'}
                  </button>
                </div>

                {/* Header Media Section */}
                {hasHeaderMedia() && (
                  <div className="broadcast-media-section">
                    <div className="broadcast-section-title">
                      Header {getHeaderMediaType().toUpperCase()} <span style={{ fontWeight: 400, color: '#666', fontSize: '0.9em' }}>(Required)</span>
                      <span className="info-icon">
                        ℹ️
                        <span className="tooltip-text">
                          <div><strong>Allowed Media Limits:</strong></div>
                          <div>• Images (JPEG/PNG): 5MB</div>
                          <div>• Video (MP4/3GPP): 16MB</div>
                          <div>• PDF: 10MB</div>
                          <div>• Audio (MP3/AAC): 16MB</div>
                        </span>
                      </span>
                    </div>

                    {!headerMediaId && !headerMediaUrl ? (
                      <div className="media-upload-container">
                        <MediaUpload
                          type={getHeaderMediaType().toUpperCase()}
                          onUpload={handleMediaUpload}
                          uploading={uploadingMedia}
                        />
                        <div className="media-divider"><span>OR</span></div>
                        <div className="media-url-input-wrapper">
                          <input
                            type="url"
                            placeholder={`Paste ${getHeaderMediaType()} URL here...`}
                            value={headerMediaUrl}
                            onChange={e => {
                              const url = e.target.value;
                              setHeaderMediaUrl(url);

                              // Basic extension validation for images
                              if (getHeaderMediaType() === 'image') {
                                if (url.match(/\.(jpeg|jpg|png|gif|webp)$/i)) {
                                  setMediaUploadError(null);
                                } else if (url && !url.match(/\.(jpeg|jpg|png|gif|webp)$/i)) {
                                  // Just a warning
                                }
                              }
                            }}
                            className="modern-input"
                          />
                        </div>
                      </div>
                    ) : (
                      <MediaUpload
                        type={getHeaderMediaType().toUpperCase()}
                        onRemove={handleRemoveMedia}
                        onUpload={handleMediaUpload}
                        previewUrl={headerMediaPreviewUrl || headerMediaUrl}
                        fileName={headerMediaId ? "Uploaded Media" : null}
                        uploading={uploadingMedia}
                        isUploaded={!!headerMediaId}
                        id={headerMediaId}
                        error={mediaUploadError}
                      />
                    )}
                  </div>
                )}

                {Object.keys(templateVariables).map(varName => (
                  <div key={varName} className="variable-row">
                    <div className="variable-label-row">
                      <span className="variable-badge">{"{{"}{varName}{"}}"}</span>
                    </div>
                    <div className="variable-input-group">
                      <select
                        value={templateVariables[varName]?.type === 'fix' ? 'fix' : templateVariables[varName]?.type === 'csv' ? `csv:${templateVariables[varName]?.value}` : ''}
                        onChange={e => {
                          const val = e.target.value;
                          if (val.startsWith('csv:')) {
                            handleVariableChange(varName, { type: 'csv', value: val.replace('csv:', '') });
                          } else if (val === 'fix') {
                            handleVariableChange(varName, { type: 'fix', value: '' });
                          } else {
                            handleVariableChange(varName, { type: '', value: '' });
                          }
                        }}
                        className="modern-select variable-type-select"
                      >
                        <option value="">Select Source</option>
                        <option value="fix">Custom Value</option>
                        {contacts.length > 0 && Object.keys(contacts[0]).map(h => (
                          h !== 'id' && <option key={h} value={`csv:${h}`}>{h} (CSV)</option>
                        ))}
                      </select>
                      {templateVariables[varName]?.type === 'fix' && (
                        <input
                          type="text"
                          placeholder="Enter value"
                          value={templateVariables[varName]?.value || ''}
                          onChange={e => handleVariableChange(varName, { type: 'fix', value: e.target.value })}
                          className="modern-input variable-value-input"
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* SPM / Catalog Inputs */}
                {(templateType === 'SPM' || templateType === 'CATALOG') && (
                  <div className="broadcast-section-group">
                    <div className="broadcast-section-title">Product Details</div>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label className="input-label">Catalog ID</label>
                        <input
                          type="text"
                          className="modern-input"
                          placeholder="Prioritized Catalog ID"
                          value={catalogDetails.catalog_id}
                          onChange={e => setCatalogDetails(prev => ({ ...prev, catalog_id: e.target.value }))}
                        />
                      </div>
                      {templateType === 'SPM' && (
                        <div style={{ flex: 1 }}>
                          <label className="input-label">Product Retailer ID</label>
                          <input
                            type="text"
                            className="modern-input"
                            placeholder="Product SKU / ID"
                            value={catalogDetails.product_retailer_id}
                            onChange={e => setCatalogDetails(prev => ({ ...prev, product_retailer_id: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                    {templateType === 'CATALOG' && (
                      <div style={{ marginBottom: '12px' }}>
                        <label className="input-label">Thumbnail Product Retailer ID (For Button)</label>
                        <input
                          type="text"
                          className="modern-input"
                          placeholder="Thumbnail Product SKU"
                          value={catalogDetails.thumbnail_product_retailer_id}
                          onChange={e => setCatalogDetails(prev => ({ ...prev, thumbnail_product_retailer_id: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* MPM Inputs */}
                {templateType === 'MPM' && (
                  <div className="broadcast-section-group">
                    <div className="broadcast-section-title">Multi-Product Sections</div>
                    <div style={{ marginBottom: 12, fontSize: '0.9em', color: '#666' }}>
                      Define sections and products for the list.
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label className="input-label">Thumbnail Product ID (Required)</label>
                      <input
                        type="text"
                        className="modern-input"
                        placeholder="Thumbnail Product Retailer ID"
                        value={catalogDetails.thumbnail_product_retailer_id}
                        onChange={e => setCatalogDetails(prev => ({ ...prev, thumbnail_product_retailer_id: e.target.value }))}
                      />
                    </div>
                    {mpmSections.map((section, sIdx) => (
                      <div key={sIdx} className="mpm-section-card">
                        <input
                          className="modern-input"
                          placeholder={`Section ${sIdx + 1} Title`}
                          value={section.title}
                          onChange={e => {
                            const newSections = [...mpmSections];
                            newSections[sIdx].title = e.target.value;
                            setMpmSections(newSections);
                          }}
                          style={{ fontWeight: 600, marginBottom: 8 }}
                        />
                        {section.product_items.map((item, pIdx) => (
                          <div key={pIdx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <input
                              className="modern-input"
                              placeholder="Product Retailer ID"
                              value={item.product_retailer_id}
                              onChange={e => {
                                const newSections = [...mpmSections];
                                newSections[sIdx].product_items[pIdx].product_retailer_id = e.target.value;
                                setMpmSections(newSections);
                              }}
                            />
                            {section.product_items.length > 1 && (
                              <button
                                className="icon-btn-danger"
                                onClick={() => {
                                  const newSections = [...mpmSections];
                                  newSections[sIdx].product_items.splice(pIdx, 1);
                                  setMpmSections(newSections);
                                }}
                              >✕</button>
                            )}
                          </div>
                        ))}
                        <button className="add-btn-small" onClick={() => {
                          const newSections = [...mpmSections];
                          if (newSections[sIdx].product_items.length < 30) {
                            newSections[sIdx].product_items.push({ product_retailer_id: '' });
                            setMpmSections(newSections);
                          } else {
                            alert("Max 30 products per section allowed");
                          }
                        }}>+ Add Product</button>

                        {mpmSections.length > 1 && (
                          <div style={{ textAlign: 'right', marginTop: 4 }}>
                            <button className="text-btn-danger" onClick={() => {
                              const newSections = [...mpmSections];
                              newSections.splice(sIdx, 1);
                              setMpmSections(newSections);
                            }}>Remove Section</button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button className="broadcast-button-secondary" style={{ width: '100%', marginTop: 8 }} onClick={() => {
                      if (mpmSections.length < 10) {
                        setMpmSections([...mpmSections, { title: '', product_items: [{ product_retailer_id: '' }] }]);
                      } else {
                        alert("Max 10 sections allowed");
                      }
                    }}>
                      + Add Section
                    </button>
                  </div>
                )}

                {/* Carousel Inputs - PRODUCT */}
                {templateType === 'CAROUSEL_PRODUCT' && (
                  <div className="broadcast-section-group">
                    <div className="broadcast-section-title">Product Carousel Cards</div>
                    {carouselCards.map((card, idx) => (
                      <div key={idx} className="mpm-section-card">
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Card {idx + 1}</div>
                        <label className="input-label">Catalog ID</label>
                        <input
                          className="modern-input"
                          value={card.catalog_id}
                          onChange={e => {
                            const newCards = [...carouselCards];
                            newCards[idx].catalog_id = e.target.value;
                            setCarouselCards(newCards);
                          }}
                          style={{ marginBottom: 8 }}
                        />
                        <label className="input-label">Product Retailer ID</label>
                        <input
                          className="modern-input"
                          value={card.product_retailer_id}
                          onChange={e => {
                            const newCards = [...carouselCards];
                            newCards[idx].product_retailer_id = e.target.value;
                            setCarouselCards(newCards);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Carousel Inputs - MEDIA */}
                {templateType === 'CAROUSEL_MEDIA' && (
                  <div className="broadcast-section-group">
                    <div className="broadcast-section-title">Media Carousel Cards</div>
                    {carouselCards.map((card, idx) => (
                      <div key={idx} className="mpm-section-card">
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Card {idx + 1} ({card.media_type})</div>

                        {!card.media_id && !card.media_url ? (
                          <div className="media-upload-container">
                            <MediaUpload
                              type={card.media_type.toUpperCase()}
                              onUpload={(e) => handleMediaUpload(e, idx)}
                              uploading={uploadingMedia}
                            />
                            <div className="media-divider"><span>OR</span></div>
                            <div className="media-url-input-wrapper">
                              <input
                                type="url"
                                placeholder={`Paste ${card.media_type} URL`}
                                className="modern-input"
                                value={card.media_url}
                                onChange={e => {
                                  const newCards = [...carouselCards];
                                  newCards[idx].media_url = e.target.value;
                                  setCarouselCards(newCards);
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <MediaUpload
                            type={card.media_type.toUpperCase()}
                            onRemove={() => {
                              const newCards = [...carouselCards];
                              newCards[idx].media_url = '';
                              newCards[idx].media_id = null;
                              newCards[idx].media_preview_url = '';
                              setCarouselCards(newCards);
                            }}
                            onUpload={(e) => handleMediaUpload(e, idx)}
                            previewUrl={card.media_preview_url || card.media_url}
                            fileName={card.media_id ? "Uploaded Media" : "Linked Media"}
                            uploading={uploadingMedia}
                            isUploaded={!!card.media_id || !!card.media_url}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="broadcast-mobile-preview-container">
                  <div className="preview-label">Message Preview</div>
                  {renderTemplatePreview()}
                </div>
              </div>
            )}
            {/* Step 3: Review & Test */}
            {step === 2 && (
              <div className="broadcast-stepper">
                <div className="review-section">
                  <h3 className="step-title">Review Campaign</h3>

                  <div className="review-cards-container">
                    <div className="review-card">
                      <div className="review-card-label">Contacts</div>
                      <div className="review-card-value">{selectedContacts.length}</div>
                    </div>
                    <div className="review-card">
                      <div className="review-card-label">Est. Cost</div>
                      <div className="review-card-value">₹{(0.8 * selectedContacts.length).toFixed(2)}</div>
                    </div>
                    <div className="review-card">
                      <div className="review-card-label">Template</div>
                      <div className="review-card-value" style={{ fontSize: '0.9em' }}>{selectedTemplate ? selectedTemplate.name : '-'}</div>
                    </div>
                  </div>

                  <div className="broadcast-mobile-preview-container">
                    <div className="preview-label">Message Preview</div>
                    {renderTemplatePreview()}
                  </div>

                  <div className="test-campaign-box">
                    <h4 className="test-box-title">⚡ Send Test Message</h4>
                    <div className="test-input-row">
                      <input
                        type="text"
                        placeholder="Phone number (e.g. 919999999999)"
                        value={testPhone}
                        onChange={e => setTestPhone(e.target.value)}
                        className="modern-input test-phone-input"
                      />
                      <button className="test-send-btn" onClick={handleSendTest} disabled={!testPhone || testSent}>
                        {testSent ? '✓ Sent' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Preview Panel - visible only on large screens, moved to right side */}
            {step > 0 && (
              <div className="broadcast-preview-panel">
                <div style={{ fontWeight: 600, marginBottom: 16, color: '#374151' }}>Message Preview</div>
                {renderTemplatePreview()}
                <div style={{ fontSize: '0.8em', color: '#6B7280', marginTop: 16, textAlign: 'center' }}>
                  Preview shows how the message might look. Actual rendering depends on the device.
                </div>
              </div>
            )}

          </div>
          {/* CTA at bottom, consistent across steps */}
          <div className="broadcast-modal-footer modern-modal-footer">
            {step > 0 && (
              <button className="broadcast-button" onClick={() => setStep(step - 1)} style={{ marginRight: 12 }}>Back</button>
            )}
            {step < 2 && (
              <button className="broadcast-button"
                onClick={handleNextStep}
                disabled={
                  (step === 0 && selectedContacts.length === 0) ||
                  (step === 1 && (!campaignName || !selectedTemplate || !areAllVariablesMapped()))
                }
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button className="broadcast-button" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : 'Send Campaign'}
              </button>
            )}
          </div>
        </div>
      </div >

      {/* CSV Import Modal */}
      {
        showCsvModal && (
          <div className="broadcast-modal-overlay">
            <div className="broadcast-modal" style={{ maxWidth: 500, width: '100%', height: 'auto', minHeight: 300 }}>
              <button className="broadcast-close-btn" onClick={() => setShowCsvModal(false)} style={{ position: 'absolute', right: 16, top: 16 }}>X</button>
              <div className="broadcast-stepper">
                <h3>Import Contacts from CSV</h3>
                <input type="file" accept=".csv" ref={fileInputRef} onChange={handleCsvUpload} />
                {csvHeaders.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h5>Map CSV Headers</h5>
                    <div>
                      <label>CSV Name Column: </label>
                      <select value={headerMap.name} onChange={e => handleHeaderMapChange(e.target.value, 'name')}>
                        <option value="">Select</option>
                        {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                    <div>
                      <label>CSV Phone Column: </label>
                      <select value={headerMap.phone} onChange={e => handleHeaderMapChange(e.target.value, 'phone')}>
                        <option value="">Select</option>
                        {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                    <button className="broadcast-button" style={{ marginTop: 16 }} onClick={handleAddCsvContacts} disabled={!headerMap.name || !headerMap.phone}>Add Contacts</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Fallback Values Modal */}
      {
        showFallbackModal && (
          <div className="broadcast-modal-overlay">
            <div className="broadcast-modal" style={{ maxWidth: 500, width: '100%', height: 'auto', minHeight: 200 }}>
              <button className="broadcast-close-btn" onClick={() => setShowFallbackModal(false)} style={{ position: 'absolute', right: 16, top: 16 }}>X</button>
              <div className="broadcast-stepper">
                <h3>Fallback Values for Empty CSV Variables</h3>
                <p>Some of the CSV variables have empty values in the selected contacts. Please provide fallback values for these variables.</p>
                {emptyCsvVars.map(varInfo => (
                  <div key={varInfo.varName} style={{ marginBottom: 10 }}>
                    <strong>Variable: {`{{${varInfo.varName}}}`}</strong>
                    <br />
                    CSV Column: <strong>{varInfo.csvColumn}</strong>
                    <br />
                    <label>Fallback Value: </label>
                    <input
                      type="text"
                      value={fallbackValues[varInfo.varName] || ''}
                      onChange={e => setFallbackValues(prev => ({ ...prev, [varInfo.varName]: e.target.value }))}
                      style={{ width: 'calc(100% - 100px)', marginLeft: 8 }}
                    />
                  </div>
                ))}
                <button className="broadcast-button" style={{ marginTop: 16 }} onClick={handleFallbackSubmit} disabled={Object.keys(fallbackValues).length !== emptyCsvVars.length}>
                  Submit Fallback Values
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Test Variables Modal */}
      {
        showTestVariablesModal && (
          <div className="broadcast-modal-overlay">
            <div className="broadcast-modal" style={{ maxWidth: 500, width: '100%', height: 'auto', minHeight: 200 }}>
              <button className="broadcast-close-btn" onClick={() => setShowTestVariablesModal(false)} style={{ position: 'absolute', right: 16, top: 16 }}>X</button>
              <div className="broadcast-stepper">
                <h3>Test Template Variables</h3>
                <p>Enter test values for the template variables to send a test message to {testPhone}:</p>

                {hasHeaderMedia() && (
                  <div style={{ marginBottom: 16, backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <h5>Header Media</h5>
                    {headerMediaId && <div style={{ marginBottom: 8, color: 'green', fontSize: '0.9em' }}>Using uploaded media from Step 2 (ID: {headerMediaId})</div>}
                    <label>Header {getHeaderMediaType()} URL {headerMediaId ? '(Optional override)' : ''}: </label>
                    <input
                      type="url"
                      placeholder={`Enter ${getHeaderMediaType()} URL for test`}
                      value={testVariables.headerMediaUrl || ''}
                      onChange={e => setTestVariables(prev => ({ ...prev, headerMediaUrl: e.target.value }))}
                      style={{ width: '100%', marginTop: '4px' }}
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                      Provide a direct URL to the {getHeaderMediaType()} file for testing
                    </small>
                  </div>
                )}

                {Object.keys(templateVariables).map(varName => (
                  <div key={varName} style={{ marginBottom: 10 }}>
                    <label>Variable {`{{${varName}}}`}: </label>
                    <input
                      type="text"
                      placeholder={`Enter test value for ${varName}`}
                      value={testVariables[varName] || ''}
                      onChange={e => setTestVariables(prev => ({ ...prev, [varName]: e.target.value }))}
                      style={{ width: '100%', marginTop: '4px' }}
                    />
                  </div>
                ))}
                <button
                  className="broadcast-button"
                  style={{ marginTop: 16 }}
                  onClick={() => {
                    setShowTestVariablesModal(false);
                    sendTestMessage(testVariables);
                  }}
                  disabled={
                    (hasHeaderMedia() && !testVariables.headerMediaUrl?.trim() && !headerMediaId) ||
                    Object.keys(templateVariables).some(varName => !testVariables[varName] || !testVariables[varName].trim())
                  }
                >
                  Send Test Message
                </button>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

// Basic Styles for MPM and Cards
const styles = document.createElement('style');
styles.innerHTML = `
  .mpm-section-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    padding: 12px;
    border-radius: 8px;
    marginBottom: 12px;
  }
  .add-btn-small {
    background: none;
    border: 1px dashed #ccc;
    color: #666;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85em;
  }
  .icon-btn-danger {
    background: #fee2e2;
    color: #dc2626;
    border: none;
    border-radius: 4px;
    width: 28px;
    cursor: pointer;
  }
  .text-btn-danger {
    background: none;
    color: #dc2626;
    border: none;
    cursor: pointer;
    font-size: 0.8em;
    text-decoration: underline;
  }
  .broadcast-section-group {
    background: white;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    margin-bottom: 20px;
  }
  .input-label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    font-size: 0.9em;
    color: #374151;
  }
`;
document.head.appendChild(styles);

export default BroadcastModal;

